import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createProduct, fetchCategories } from "../../../RTK/productSlice";
import axios from "axios";
import { 
  Plus,
  X,
  Upload,
  Sparkles,
  Package,
  DollarSign,
  Percent,
  CheckCircle,
  XCircle,
  Image as ImageIcon,
  FileText,
  Tag,
  LayoutGrid
} from "lucide-react";

const CategoryDetailsPopup = ({ selectedType, details, setDetailsPopup }) => {
  const dispatch = useDispatch();
  const { categoryError } = useSelector((state) => state.products);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({ image: false });
  const [formData, setFormData] = useState({
    category: "",
    image: "",
    imageFile: null,
    name: "",
    description: "",
    status: "available",
    product_type: "",
    price: "",
    commission_rate: "",
    product_obj: selectedType?.toLowerCase(),
  });

  useEffect(() => {
    if (selectedType) {
      dispatch(fetchCategories(selectedType));
    }
  }, [selectedType, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: URL.createObjectURL(file),
        imageFile: file,
      }));
      setFormError({ ...formError, image: false });
    }
  };

  const handleGenerateDescription = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/Products/GenerateDescription/`,
        { name: formData.name }, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      setFormData((prev) => ({
        ...prev,
        description: response.data.description,
      }));
    } catch (error) {
      console.error("Error generating description:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.imageFile) {
      setFormError({ ...formError, image: true });
      return;
    }
    dispatch(createProduct({
      ...formData,
      image: formData.imageFile,
    })).then(() => setDetailsPopup(false));
  };

  if (categoryError) {
    return (
      <div className="text-rose-500 bg-rose-500/10 p-4 rounded-xl border border-rose-500/20">
        خطأ: {categoryError}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[20000] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl" dir="rtl">
        <div className="bg-gradient-to-b bg-gray-900 rounded-2xl border border-gray-800/50 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="relative flex justify-between items-center p-6 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-gray-700/50 after:to-transparent">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Package className="w-6 h-6 text-[#FEC111]" />
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                تفاصيل {selectedType === 'quantity' ? 'الكمية' : selectedType === 'package' ? 'الباقة' : 'المخزون'}
              </span>
            </h2>
            <button
              onClick={() => setDetailsPopup(false)}
              className="text-gray-400 w-10 h-10 rounded-full flex items-center justify-center bg-gray-800/50 hover:bg-[#FEC111] hover:text-black transition-all duration-300 hover:scale-105"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content */}
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload */}
              <div className="flex flex-col items-center p-6 bg-slate-800/50 rounded-xl border border-gray-800/50">
                <div className="relative group cursor-pointer">
                  <img
                    src={formData.image || "avatar.png"}
                    alt="Preview"
                    className="w-24 h-24 rounded-full border-2 border-[#FEC111] object-cover"
                  />
                  <label
                    htmlFor="file"
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Upload className="w-6 h-6 text-white" />
                  </label>
                </div>
                <input
                  type="file"
                  id="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {formError.image && (
                  <p className="text-rose-500 text-sm mt-2">
                    يرجى تحميل صورة
                  </p>
                )}
              </div>

              

              {/* Two-Column Grid Layout */}
              <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                {/* Name Input */}
                <div className="relative">
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder=" الاسم"
                      required
                      className="w-full px-4  py-3 rounded-xl bg-slate-800/50 text-white border border-gray-800/50 focus:border-[#FEC111]/50 focus:ring-1 focus:ring-[#FEC111]/50 transition-colors pr-4"
                    />
                    <Tag className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>

                {/* Price Input */}
                <div className="relative">
                  <div className="relative">
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder=" السعر"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-slate-800/50 text-white border border-gray-800/50 focus:border-[#FEC111]/50 focus:ring-1 focus:ring-[#FEC111]/50 transition-colors pr-4"
                    />
                    <DollarSign className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>

                {/* Commission Rate Input */}
                <div className="relative">
                  <div className="relative">
                    <input
                      type="number"
                      name="commission_rate"
                      value={formData.commission_rate}
                      onChange={handleChange}
                      placeholder=" نسبة العمولة"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-slate-800/50 text-white border border-gray-800/50 focus:border-[#FEC111]/50 focus:ring-1 focus:ring-[#FEC111]/50 transition-colors pr-4"
                    />
                    <Percent className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>

                {/* Type Selection */}
                <div className="relative">
                  <div className="relative">
                    <select
                      name="product_type"
                      value={formData.product_type}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-slate-800/50 text-white border border-gray-800/50 focus:border-[#FEC111]/50 focus:ring-1 focus:ring-[#FEC111]/50 transition-colors appearance-none pr-4"
                    >
                      <option value="" disabled>اختر النوع</option>
                      <option value="quantity">كمية</option>
                      <option value="package">باقة</option>
                      <option value="stock">مخزون</option>
                    </select>
                    <LayoutGrid className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>

                {/* Status Selection */}
                <div className="relative">
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 text-white border border-gray-800/50 focus:border-[#FEC111]/50 focus:ring-1 focus:ring-[#FEC111]/50 transition-colors"
                  >
                    <option value="available">متوفر</option>
                    <option value="unavailable">غير متوفر</option>
                  </select>
                </div>

                {/* Category Selection */}
              <div className="">
                <select
                  name="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-slate-800/50 text-white border border-gray-800/50 focus:border-[#FEC111]/50 focus:ring-1 focus:ring-[#FEC111]/50 transition-colors"
                >
                  <option value="" disabled>اختر التصنيف</option>
                  {details.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name || "تصنيف بدون اسم"}
                    </option>
                  ))}
                </select>
              </div>

              </div>

              {/* Description - Full Width */}
              <div className="w-full">
                <div className="space-y-2">
                  <textarea
                    required
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder=" الوصف"
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 text-white border border-gray-800/50 focus:border-[#FEC111]/50 focus:ring-1 focus:ring-[#FEC111]/50 transition-colors h-24 resize-none"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateDescription}
                    disabled={loading || !formData.name}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-[#FEC111] text-black rounded-xl hover:bg-[#FEC111]/90 transition-all duration-300 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="w-5 h-5" />
                    {loading ? "جارِ التوليد..." : "توليد وصف ذكي"}
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setDetailsPopup(false)}
                  className="flex items-center justify-center gap-2 py-3 px-6 bg-slate-800/50 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all duration-300 font-bold"
                >
                  <X className="w-5 h-5" />
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 py-3 px-6 bg-[#FEC111] text-black rounded-xl hover:bg-[#FEC111]/90 transition-all duration-300 font-bold"
                >
                  <Plus className="w-5 h-5" />
                  إنشاء المنتج
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryDetailsPopup;